import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { AlertsService } from 'src/app/providers/services/alerts.service';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { ContainersService } from 'src/app/providers/services/containers.service';
import { LoadingStationsService } from 'src/app/providers/services/loading-stations.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { ContainerMission } from 'src/app/storage/models/container-mission.model';
import { Container } from 'src/app/storage/models/container.model';
import { LoadingStation } from 'src/app/storage/models/loading-station.model';
import { Mission } from 'src/app/storage/models/mission.model';
import { ProductMission } from 'src/app/storage/models/product-mission.model';
import { IUpdateMissionProductParams } from '../../common/update-mission-product/update-mission-product.component';

@Component({
    selector: 'app-product-config',
    templateUrl: './product-config.component.html',
    styleUrls: ['./product-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductConfigComponent implements OnInit, OnDestroy {
  mission: Mission;
  containers: ContainerMission[] = [];
  loadingStations: LoadingStation[] = [];
  addingContainer = false;

  show: boolean = true;

  updateMissionProductParams: IUpdateMissionProductParams = {
    display: null,
    mission: null,
    missionProduct: null
  };

  private missionSubscription: Subscription;
  private containersSubscription: Subscription;

  constructor(
    private missionsService: MissionsService,
    private containersService: ContainersService,
    private loadingStationsService: LoadingStationsService,
    private alertsService: AlertsService,
    private translateService: TranslateService,
    private navigationService: NavigationService,
    private confirmationModalService: ConfirmationModalService
  ) { }

  ngOnInit(): void {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      await this.init(mission);
    });

    this.containersSubscription = this.containersService.missionContainers$.subscribe(async (containers: ContainerMission[]) => {
      this.containers = containers;
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
    if (this.containersSubscription) { this.containersSubscription.unsubscribe(); }
  }

  async init(mission: Mission): Promise<void> {
    this.mission = mission;

    // Set global loading stations, navigate back to mission configuration if  couldn't be fetched
    const loadingStations = await this.loadingStationsService.getLoadingStations();
    if (loadingStations != null) {
      this.loadingStations = loadingStations.sort((a, b) => {
        if (a.marketname === b.marketname) {
          return a.name > b.name ? 1 : -1;
        } else if (a.marketname === this.mission.marketname) {
          return -1;
        } else if (b.marketname === this.mission.marketname) {
          return 1;
        } else {
          return a.name > b.name ? 1 : -1;
        }
      });
    }
    else { await this.backToMissionConfig(); }
  }

  refresh(): void {
    this.show = false;
    setTimeout(() => this.show = true);
  }


  getLoadedQuantity(missionProduct: ProductMission): number {
    return this.containers.filter(container => container.productName === missionProduct.productName)
                          .reduce((acc, cur) => {
                            return acc + cur.containermissionLoadedDMDSQuantity
                          }, 0);
  }

  async backToMissionConfig() {
    await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_CONFIG);
  }

  async setContainerQuantity(quantity: number | null, container: ContainerMission): Promise<void> {
    // Blur and Enter both submit: only save an actual change
    if (quantity === container.loadedQty) { return; }
    const value = Math.floor(quantity);

    if (quantity == null || isNaN(value)) {
      container.containermissionLoadedDMDSQuantity = 0;
    } else {
      const updatedContainer = new ContainerMission(container);
      updatedContainer.containermissionLoadedDMDSQuantity = value;

      // Update mission's container
      const isContainerUpdated = await this.containersService.updateMissionContainer(this.mission.missionId, updatedContainer);

      // Set new quantity or reset to current value
      container.containermissionLoadedDMDSQuantity = isContainerUpdated ?
                                                    value :
                                                    container.containermissionLoadedDMDSQuantity;
    }

    this.refresh();
  }

  async setContainerLoadingStation(loadingStationName: string, container: ContainerMission): Promise<void> {

    const updatedContainer = new ContainerMission(container);
    updatedContainer.loadingstationName = loadingStationName;

    // Update mission's container
    const isContainerUpdated = await this.containersService.updateMissionContainer(this.mission.missionId, updatedContainer);
    
    // Set new quantity or reset to current value
    container.loadingstationName = isContainerUpdated ?
                                  loadingStationName :
                                  container.loadingstationName;

    this.refresh();
  }

  async setContainerProduct(productName: string, container: ContainerMission): Promise<void> {

    const updatedContainer = new ContainerMission(container);
    updatedContainer.productName = productName;

    // Update mission's container
    const isContainerUpdated = await this.containersService.updateMissionContainer(this.mission.missionId, updatedContainer);
    
    // Set new quantity or reset to current value
    container.productName = isContainerUpdated ?
                            productName :
                            container.productName;

    this.refresh();
  }

  openContainerModal(): void {
    this.addingContainer = true;
  }

  async closeContainerModal(container?: Container): Promise<void> {
    this.addingContainer = false;

    // Add container if it is provided
    if (container) {
      !this.containers.find(ctn => ctn.containerId === container.containerId) ?
        await this.addContainer(container):
        await this.containerAlreadySelected();
    }
  }

  async containerAlreadySelected(): Promise<void> {
    const translations = await this.translateService.get('PRODUCT').toPromise();
    this.alertsService.info({
      title: translations['LABEL'],
      content: translations['CONTAINER_ALREADY_SELECTED']
    });
  }

  async addContainer(container: Container): Promise<void> {
    // Set current product if containers are already selected
    const productName = this.containers.length > 0 ?
                        this.containers[0].productName :
                        this.mission.products[0].productName;

    const containerMission = new ContainerMission({
      ...container,
      containermissionpk: null,
      containermissionLoadedDMDSQuantity: 0,
      loadingstationName: this.loadingStations[0].name,
      productName: productName,
      createdOn: Math.floor(Date.now() / 1000).toString(),
      createdBy: null,
      changedOn: null,
      changedBy: null
    });

    const isContainerAdded = await this.containersService.addMissionContainer(
      this.mission.missionId,
      containerMission
    );

    if (isContainerAdded) {
      // Refresh containers
      const containers = await this.containersService.getMissionContainers(this.mission.missionId);
      if (containers) { this.containersService.missionContainers$.next(containers); }

      if (this.mission.productstatus !== CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS) {
        // Set product status to 'IN PROGRESS'
        await this.setProductStatus(CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS);
      }
    }
  }

  async removeMissionContainer(missionContainer: ContainerMission): Promise<void> {
    const isContainerRemoved = await this.containersService.removeMissionContainer(
      this.mission.missionId,
      missionContainer
    );

    if (isContainerRemoved) {
      // Remove container from behavior subject
      this.containersService.missionContainers$.next(
        this.containers.filter(ctn => ctn.containerpk !== missionContainer.containerpk)
      );
    }
  }

  async toggleProductStatus(): Promise<void> {
    const status = (this.mission.productstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED) ?
                    CONSTANTS.MISSION.CONFIG_STATUS.IN_PROGRESS :
                    CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;

    // Confirm
    if (status === CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED ) {
      const translations = await this.translateService.get('PRODUCT').toPromise();
      let title = `<strong>${translations['STATUS_COMPLETE']}</strong>`;

      // Check for additional warnings
      const containersInError = this.containers.filter(container => container.isInError);
      const loadedProductsInError = this.mission.products.filter((product: ProductMission) =>
        product.productmissionLoadedExpectedMassTotal > this.getLoadedQuantity(product)
      );

      const hasWarnings = (containersInError.length > 0 || loadedProductsInError.length > 0)

      if (hasWarnings) {
        title +=`<br><div><img src="assets/images/error-icon.svg" width="50" height="50"/></div>`
      }

      if (containersInError.length > 0) {
        title += `<br><div style="color:red">${translations['CONTAINERS_IN_WARNING']}</div>`;
        containersInError.map(container => {
          title += `<div style="color:red">- ${
            translations['CONTAINER_IN_WARNING']
            .replace('{CONTAINER_ID}', container.containerId)
            .replace('{CONTAINER_QTY}', container.loadedQty)
            .replace('{CONTAINER_LIMIT}', container.limit)
          }</div>`;
        });
      }

      if (loadedProductsInError.length > 0) { 
        title += `<br>`;

        loadedProductsInError.map((product: ProductMission) => {
          title += `<div style="color:red">${translations['LOADED_QTY_LOW']}</div>`.replace('{PRODUCT}', product.productName);
        });
      }

      // Open confirmation modal
      this.confirmationModalService.openModal({
        title: title,
        hideControls: hasWarnings,
        buttons: [
          {
            text: await this.translateService.get('CONFIRM').toPromise(),
            method: async () => {
              this.confirmationModalService.closeModal();
              await this.setProductStatus(status);
            }
          },
          {
            text: await this.translateService.get('CANCEL').toPromise(),
            method: async () => { this.confirmationModalService.closeModal(); }
          }
        ]
      });
    } else {
      await this.setProductStatus(status);
    }
  }

  async setProductStatus(status: number): Promise<void> {
    // Update product status
    const isStatusUpdated = await this.missionsService.updateMissionProductStatus(this.mission.missionId, status);

    // If update succeeded, set mission product status
    if (isStatusUpdated) {
      this.mission.productstatus = status;
    }
  }

  updateMissionProduct(missionProduct: ProductMission): void {
    this.updateMissionProductParams = {
      display: true,
      mission: this.mission,
      missionProduct: missionProduct
    };
  }

  isProductComplete(): boolean {
    return this.mission.productstatus >= CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED;
  }

}
